<template>
  <div
    class="selected-platform-card"
    :draggable="draggableCard"
    @dragstart="handleDragStart"
  >
    <div v-if="draggableCard" class="selected-platform-card__drag-tooltip">
      Drag and drop card to select
    </div>

    <button
      v-if="removable"
      type="button"
      class="selected-platform-card__remove-indicator"
      aria-label="Remove platform"
      @click.stop="handleRemove"
    >
      <SVGIcon
        class="selected-platform-card__remove-icon"
        :iconName="'close'"
        :width="'24px'"
        :height="'24px'"
      />
    </button>

    <div v-if="vendorLogoSrc" class="selected-platform-card__vendor-corner">
      <img
        :src="vendorLogoSrc"
        :alt="vendorName"
        class="selected-platform-card__vendor-corner-logo"
      >
    </div>

    <div class="selected-platform-card__title">
      {{ platformName }}
    </div>

    <div v-if="platformDescription" class="selected-platform-card__description">
      {{ platformDescription }}
      <hr class="selected-platform-card__description-divider">
    </div>

    <div class="selected-platform-card__content-row">
      <div class="selected-platform-card__image-wrap">
        <img
          v-if="platformImageSrc"
          :src="platformImageSrc"
          :alt="platformName"
          class="selected-platform-card__image"
        >
        <div v-else class="selected-platform-card__image-fallback">
          No platform image
        </div>
      </div>

      <div class="selected-platform-card__details">
        <div class="selected-platform-card__specs">
          <div v-if="specRows.length > 0" class="selected-platform-card__spec-grid">
            <div
              v-for="(row, index) in specRows"
              :key="`${row.key}-${index}`"
              class="selected-platform-card__spec-row"
            >
              <div class="selected-platform-card__spec-key">{{ row.key }}</div>
              <div class="selected-platform-card__spec-value">{{ row.value }}</div>
            </div>
          </div>
          <div v-else class="selected-platform-card__spec-empty">
            No specs available
          </div>
        </div>
      </div>
    </div>

    <PlatformRuntimeSettings :detectedPlatform="detectedPlatform" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import SVGIcon from '@/components/SVGIcon.vue';
import PlatformRuntimeSettings from '@/components/Platforms/PlatformRuntimeSettings.vue';

const props = defineProps({
  vendor: {
    type: Object,
    default: () => ({}),
  },
  platform: {
    type: Object,
    default: () => ({}),
  },
  host: {
    type: String,
    default: '',
  },
  draggableCard: {
    type: Boolean,
    default: false,
  },
  removable: {
    type: Boolean,
    default: false,
  },
  detectedPlatform: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(['remove']);

const vendorName = computed(() => props.vendor?.name || props.vendor?.code || 'Vendor');
const platformName = computed(() => props.platform?.name || props.platform?.code || 'Platform');
const platformDescription = computed(() => String(props.platform?.description || '').trim());
const hostValue = computed(() => String(props.host || '').trim());
const specRows = computed(() => {
  const rows = normalizeSpecs(props.platform?.specs);
  if (hostValue.value) {
    rows.push({
      key: 'Host',
      value: hostValue.value,
    });
  }

  return rows;
});

const vendorLogoSrc = computed(() => {
  const directSource = resolveImageSource(props.vendor, ['logo', 'logo_url', 'logoUrl', 'image', 'image_url', 'imageUrl']);
  if (directSource) {
    return directSource;
  }

  const vendorCode = String(props.vendor?.code || '').trim();
  return vendorCode ? `/vendor/${encodeURIComponent(vendorCode)}/logo` : '';
});

const platformImageSrc = computed(() => {
  const directSource = resolveImageSource(props.platform, ['image', 'image_url', 'imageUrl', 'logo', 'logo_url', 'logoUrl']);
  if (directSource) {
    return directSource;
  }

  const platformCode = String(props.platform?.code || '').trim();
  return platformCode ? `/platform/${encodeURIComponent(platformCode)}/image` : '';
});

function resolveImageSource(source, keys) {
  if (!source || typeof source !== 'object') {
    return '';
  }

  for (const key of keys) {
    const value = source[key];
    const normalized = normalizeImageValue(value);
    if (normalized) {
      return normalized;
    }
  }

  return '';
}

function normalizeImageValue(value) {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    if (trimmed.startsWith('data:image') || trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
      return trimmed;
    }
    return `data:image/${detectMimeTypeFromBase64(trimmed)};base64,${trimmed}`;
  }

  if (Array.isArray(value) && value.length > 0) {
    const binary = new Uint8Array(value);
    return bytesToDataUrl(binary);
  }

  if (value instanceof Uint8Array) {
    return bytesToDataUrl(value);
  }

  return '';
}

function bytesToDataUrl(bytes) {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  const mimeType = detectMimeTypeFromText(binary);
  return `data:image/${mimeType};base64,${btoa(binary)}`;
}

function detectMimeTypeFromBase64(base64Value) {
  try {
    return detectMimeTypeFromText(atob(base64Value));
  } catch (error) {
    return 'png';
  }
}

function detectMimeTypeFromText(text) {
  const normalized = String(text || '').trimStart();
  if (normalized.startsWith('<svg') || normalized.startsWith('<?xml') || normalized.includes('<svg')) {
    return 'svg+xml';
  }

  return 'png';
}

function normalizeSpecs(specs) {
  if (specs && typeof specs === 'object' && !Array.isArray(specs)) {
    return Object.entries(specs)
      .filter(([key, value]) => key && value !== undefined && value !== null && String(value).trim() !== '')
      .map(([key, value]) => ({
        key: formatSpecKey(key),
        value: String(value),
      }));
  }

  if (Array.isArray(specs)) {
    return specs
      .map((item, index) => ({
        key: `Item ${index + 1}`,
        value: String(item),
      }))
      .filter((row) => row.value.trim() !== '');
  }

  if (typeof specs === 'string') {
    const trimmed = specs.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return normalizeSpecs(parsed);
      }
    } catch (error) {
      // Continue with text parsing when specs is not valid JSON.
    }

    const pairs = trimmed
      .split('|')
      .map((chunk) => chunk.trim())
      .filter(Boolean)
      .map((chunk) => {
        const separatorIndex = chunk.indexOf(':');
        if (separatorIndex === -1) {
          return { key: 'Spec', value: chunk };
        }

        const key = chunk.slice(0, separatorIndex).trim();
        const value = chunk.slice(separatorIndex + 1).trim();
        return {
          key: formatSpecKey(key || 'Spec'),
          value,
        };
      })
      .filter((row) => row.value);

    return pairs;
  }

  return [];
}

function formatSpecKey(key) {
  return String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function handleDragStart(event) {
  if (!props.draggableCard || !event?.dataTransfer) {
    return;
  }

  const payload = {
    vendor: props.vendor || {},
    platform: props.platform || {},
  };

  const serialized = JSON.stringify(payload);
  event.dataTransfer.effectAllowed = 'copy';
  event.dataTransfer.setData('application/platform-item', serialized);
  event.dataTransfer.setData('text/plain', serialized);
}

function handleRemove() {
  emit('remove', {
    vendor: props.vendor || {},
    platform: props.platform || {},
  });
}
</script>

<style lang="scss" scoped>
.selected-platform-card {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
  margin : 8px;
  gap: 4px;
  position: relative;
  min-width: 400px;
  max-width: 100%;
  min-height: 0;
  padding: 12px;
  border-radius: 4px;
  text-align: left;
  cursor: default;
  @include themify() {
    background: #ffffff;
    color: #2f2f2f;
    border: 1px solid #d5dbe6;
  }

  &:hover {
    box-shadow: 0 0 0 1px #2563eb;
  }
}

.selected-platform-card[draggable='true'] {
  cursor: grab;
}

.selected-platform-card__drag-tooltip {
  position: absolute;
  top: -34px;
  left: 12px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s ease;
  z-index: 4;
  white-space: nowrap;
  @include themify() {
    background: rgba(20, 20, 20, 0.9);
    color: #ffffff;
  }
}

.selected-platform-card[draggable='true']:hover .selected-platform-card__drag-tooltip {
  opacity: 1;
}

.selected-platform-card__remove-indicator {
  position: absolute;
  top: calc(50% - 60px);
  left: 50%;
  transform: translate(-50%, -50%);
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: auto;
  transition: opacity 0.16s ease;
  z-index: 3;
  cursor: pointer;
  @include themify() {
    background: rgba(0, 0, 0, 0.6);
    color: #ffffff;
  }
}

.selected-platform-card__remove-icon {
  width: 24px;
  height: 24px;
  color: #ffffff;
}

.selected-platform-card:hover .selected-platform-card__remove-indicator {
  opacity: 1;
}

.selected-platform-card__vendor-corner {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 64px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selected-platform-card__vendor-corner-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.selected-platform-card__title {
  width: 100%;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
  padding-top: 4px;
  padding-bottom: 4px;
}

.selected-platform-card__details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.selected-platform-card__content-row {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 10px;
  width: 100%;
  align-items: start;
}

.selected-platform-card__description,
.selected-platform-card__specs {
  font-size: 0.78rem;
  line-height: 1.35;
  text-align: left;
}

.selected-platform-card__description {
  width: 100%;
  padding-top: 8px;
  padding-bottom: 4px;
  font-style: italic;
  color: rgb(12, 59, 99);
}

.selected-platform-card__description-divider {
  margin: 6px 0;
  border: 0;
  border-top: 1px solid #d5dbe6;
}

.selected-platform-card__title + .selected-platform-card__description {
  margin-top: -14px;
}

.selected-platform-card__description + .selected-platform-card__content-row {
  margin-top: -10px;
}

.selected-platform-card__specs {
  @include themify() {
    color: #555555;
  }
}

.selected-platform-card__spec-grid {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.selected-platform-card__spec-row {
  display: grid;
  grid-template-columns: minmax(90px, 120px) minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}

.selected-platform-card__spec-key {
  font-weight: 600;
}

.selected-platform-card__spec-value {
  word-break: break-word;
}

.selected-platform-card__spec-empty {
  font-size: 0.78rem;
  line-height: 1.35;
}

.selected-platform-card__image-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-height: 0;
  padding: 0;
  border-radius: 0;
}

.selected-platform-card__image {
  display: block;
  margin: 0;
  width: auto;
  max-width: 120px;
  max-height: 120px;
  object-fit: contain;
}

.selected-platform-card__image-fallback {
  width: 100%;
  font-size: 0.78rem;
  text-align: left;
  @include themify() {
    color: #555555;
  }
}

.selected-platform-card__runtime-divider {
  margin: 12px 0 8px;
  border: none;
  border-top: 1px solid;
  @include themify() {
    border-color: rgba(0, 0, 0, 0.12);
  }
}

.selected-platform-card__runtime {
  padding: 0 2px;
}

.selected-platform-card__runtime-heading {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  @include themify() {
    color: #888888;
  }
}
</style>
